import React from "react";

// ─── Design Tokens ─────────────────────────────────────────────────────────
export const C = {
  bg: "#070d1a",
  surface: "#0c1424",
  surfaceHigh: "#0f1a2e",
  surfaceMid: "#111d33",
  border: "rgba(45,127,249,0.15)",
  borderBright: "rgba(45,127,249,0.35)",
  borderGlow: "rgba(45,127,249,0.6)",
  blue: "#2d7ff9",
  blueDim: "rgba(45,127,249,0.1)",
  blueMid: "rgba(45,127,249,0.2)",
  cyan: "#06b6d4",
  cyanDim: "rgba(6,182,212,0.1)",
  purple: "#8b5cf6",
  purpleDim: "rgba(139,92,246,0.1)",
  green: "#10b981",
  greenDim: "rgba(16,185,129,0.1)",
  red: "#ef4444",
  redDim: "rgba(239,68,68,0.1)",
  amber: "#f59e0b",
  amberDim: "rgba(245,158,11,0.1)",
  pink: "#ec4899",
  text: "#e8edf5",
  textSub: "#a8b8d0",
  textMuted: "#5a7399",
  node: {
    trigger: { bg: "rgba(45,127,249,0.12)", border: "rgba(45,127,249,0.4)", color: "#2d7ff9" },
    filter: { bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.4)", color: "#f59e0b" },
    action: { bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.4)", color: "#10b981" },
    escalation: { bg: "rgba(239,68,68,0.12)", border: "rgba(239,68,68,0.4)", color: "#ef4444" },
    delay: { bg: "rgba(139,92,246,0.12)", border: "rgba(139,92,246,0.4)", color: "#8b5cf6" },
  },
};

// ─── Global CSS (inject via <style> in root) ───────────────────────────────
export const GLOBAL_STYLES = `
  @keyframes pulse-dot {
    0%,100% { opacity:1; transform:scale(1); }
    50%      { opacity:0.5; transform:scale(0.85); }
  }
  @keyframes glow-breathe {
    0%,100% { box-shadow: 0 0 8px rgba(45,127,249,0.25); }
    50%     { box-shadow: 0 0 22px rgba(45,127,249,0.55), 0 0 44px rgba(45,127,249,0.15); }
  }
  @keyframes glow-green {
    0%,100% { box-shadow: 0 0 6px rgba(16,185,129,0.25); }
    50%     { box-shadow: 0 0 18px rgba(16,185,129,0.5); }
  }
  @keyframes glow-red {
    0%,100% { box-shadow: 0 0 6px rgba(239,68,68,0.3); }
    50%     { box-shadow: 0 0 18px rgba(239,68,68,0.6); }
  }
  @keyframes flow-dash {
    to { stroke-dashoffset: -40; }
  }
  @keyframes slide-up {
    from { opacity:0; transform:translateY(10px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes slide-in-right {
    from { opacity:0; transform:translateX(16px); }
    to   { opacity:1; transform:translateX(0); }
  }
  @keyframes shimmer {
    0%   { background-position: -400% center; }
    100% { background-position: 400% center; }
  }
  @keyframes spin {
    from { transform:rotate(0deg); }
    to   { transform:rotate(360deg); }
  }
  @keyframes float {
    0%,100% { transform:translateY(0); }
    50%     { transform:translateY(-5px); }
  }
  @keyframes gradient-border {
    0%   { background-position:0% 50%; }
    50%  { background-position:100% 50%; }
    100% { background-position:0% 50%; }
  }
  @keyframes ai-dot {
    0%,80%,100% { transform:translateY(0); opacity:0.4; }
    40%         { transform:translateY(-5px); opacity:1; }
  }
  @keyframes progress-bar {
    from { width:0%; }
    to   { width:var(--progress); }
  }
  @keyframes node-appear {
    from { opacity:0; transform:scale(0.9); }
    to   { opacity:1; transform:scale(1); }
  }
  @keyframes halo-pulse {
    0%,100% { opacity:0.3; transform:scale(1); }
    50%     { opacity:0; transform:scale(1.6); }
  }
  ::-webkit-scrollbar { width:4px; height:4px; }
  ::-webkit-scrollbar-track { background:transparent; }
  ::-webkit-scrollbar-thumb { background:rgba(45,127,249,0.2); border-radius:2px; }
  ::-webkit-scrollbar-thumb:hover { background:rgba(45,127,249,0.4); }
  * { box-sizing:border-box; }
`;

// ─── GlassCard ─────────────────────────────────────────────────────────────
type GlassCardProps = {
  children: React.ReactNode;
  className?: string;
  hover?: boolean;
  glow?: boolean;
  animated?: boolean;
  accent?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
};

export function GlassCard({ children, className = "", hover = true, glow = false, animated = false, accent, style = {}, onClick }: GlassCardProps) {
  const glowStyle = glow
    ? { animation: `glow-breathe 3s ease-in-out infinite` }
    : {};
  return (
    <div
      className={`relative rounded-2xl transition-all duration-300 ${hover && onClick ? "cursor-pointer hover:scale-[1.01]" : ""} ${className}`}
      style={{
        background: `linear-gradient(135deg, ${C.surfaceHigh}dd, ${C.surface}ee)`,
        border: `1px solid ${accent ? accent + "40" : C.border}`,
        backdropFilter: "blur(12px)",
        boxShadow: glow
          ? `0 0 24px rgba(45,127,249,0.18), inset 0 1px 0 rgba(255,255,255,0.05)`
          : `0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.03)`,
        ...glowStyle,
        ...style,
      }}
      onClick={onClick}
    >
      {animated && (
        <div
          className="absolute inset-0 rounded-2xl opacity-20 pointer-events-none"
          style={{
            background: `linear-gradient(90deg, transparent, ${C.blue}30, transparent)`,
            backgroundSize: "200%",
            animation: "shimmer 3s linear infinite",
          }}
        />
      )}
      {children}
    </div>
  );
}

// ─── AnimatedBorderCard ────────────────────────────────────────────────────
export function AnimatedBorderCard({ children, className = "", style = {} }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={`relative p-px rounded-2xl ${className}`} style={style}>
      <div
        className="absolute inset-0 rounded-2xl"
        style={{
          background: `linear-gradient(135deg, ${C.blue}, ${C.cyan}, ${C.purple}, ${C.blue})`,
          backgroundSize: "300% 300%",
          animation: "gradient-border 4s ease infinite",
          opacity: 0.6,
        }}
      />
      <div className="relative rounded-2xl" style={{ background: C.surface }}>
        {children}
      </div>
    </div>
  );
}

// ─── Badge ─────────────────────────────────────────────────────────────────
type BadgeColor = "blue" | "green" | "red" | "amber" | "purple" | "cyan" | "pink" | "gray";

const BADGE_MAP: Record<BadgeColor, { bg: string; text: string; border: string }> = {
  blue:   { bg: "rgba(45,127,249,0.15)",  text: "#7eb8ff", border: "rgba(45,127,249,0.3)" },
  green:  { bg: "rgba(16,185,129,0.15)",  text: "#34d399", border: "rgba(16,185,129,0.3)" },
  red:    { bg: "rgba(239,68,68,0.15)",   text: "#f87171", border: "rgba(239,68,68,0.3)" },
  amber:  { bg: "rgba(245,158,11,0.15)",  text: "#fbbf24", border: "rgba(245,158,11,0.3)" },
  purple: { bg: "rgba(139,92,246,0.15)",  text: "#a78bfa", border: "rgba(139,92,246,0.3)" },
  cyan:   { bg: "rgba(6,182,212,0.15)",   text: "#22d3ee", border: "rgba(6,182,212,0.3)" },
  pink:   { bg: "rgba(236,72,153,0.15)",  text: "#f472b6", border: "rgba(236,72,153,0.3)" },
  gray:   { bg: "rgba(90,115,153,0.15)",  text: "#a8b8d0", border: "rgba(90,115,153,0.3)" },
};

export function Badge({ children, color = "blue", className = "" }: { children: React.ReactNode; color?: BadgeColor; className?: string }) {
  const s = BADGE_MAP[color];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium ${className}`}
      style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}` }}
    >
      {children}
    </span>
  );
}

// ─── StatusDot ─────────────────────────────────────────────────────────────
type DotStatus = "online" | "offline" | "warning" | "pending" | "syncing";

export function StatusDot({ status, size = "sm" }: { status: DotStatus; size?: "xs" | "sm" | "md" }) {
  const colorMap: Record<DotStatus, string> = {
    online: C.green, offline: C.red, warning: C.amber, pending: C.blue, syncing: C.cyan,
  };
  const sizeMap = { xs: "w-1.5 h-1.5", sm: "w-2 h-2", md: "w-3 h-3" };
  const c = colorMap[status];
  return (
    <span className="relative inline-flex items-center justify-center">
      <span
        className={`absolute rounded-full ${sizeMap[size]}`}
        style={{ background: c, opacity: 0.4, animation: "halo-pulse 2s ease-out infinite", transform: "scale(1)" }}
      />
      <span
        className={`relative rounded-full ${sizeMap[size]}`}
        style={{ background: c, boxShadow: `0 0 6px ${c}`, animation: status === "online" ? "pulse-dot 2s ease-in-out infinite" : undefined }}
      />
    </span>
  );
}

// ─── Button ────────────────────────────────────────────────────────────────
type BtnVariant = "primary" | "secondary" | "ghost" | "danger" | "outline" | "glow";
type BtnSize = "xs" | "sm" | "md" | "lg";

export function Btn({
  children, variant = "primary", size = "md", onClick, className = "", icon, disabled = false,
}: {
  children: React.ReactNode; variant?: BtnVariant; size?: BtnSize;
  onClick?: () => void; className?: string; icon?: React.ReactNode; disabled?: boolean;
}) {
  const base = "inline-flex items-center gap-2 font-medium rounded-xl transition-all duration-200 cursor-pointer select-none whitespace-nowrap";
  const sizes: Record<BtnSize, string> = {
    xs: "px-2 py-1 text-[11px]", sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-sm"
  };
  const styles: Record<BtnVariant, React.CSSProperties> = {
    primary:   { background: `linear-gradient(135deg, ${C.blue}, #1a5fd4)`, color: "#fff", boxShadow: `0 0 16px rgba(45,127,249,0.35)` },
    glow:      { background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, color: "#fff", boxShadow: `0 0 20px rgba(45,127,249,0.5)` },
    secondary: { background: C.surfaceHigh, color: C.textSub, border: `1px solid ${C.border}` },
    ghost:     { background: "transparent", color: C.textSub },
    danger:    { background: C.redDim, color: "#f87171", border: "1px solid rgba(239,68,68,0.25)" },
    outline:   { background: "transparent", color: C.blue, border: `1px solid ${C.borderBright}` },
  };
  return (
    <button
      className={`${base} ${sizes[size]} ${disabled ? "opacity-40 cursor-not-allowed" : "hover:brightness-110 active:scale-95"} ${className}`}
      style={styles[variant]}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
    >
      {icon}{children}
    </button>
  );
}

// ─── Input ─────────────────────────────────────────────────────────────────
export function Input({
  placeholder, value, onChange, prefix, suffix, className = "", type = "text", rows,
}: {
  placeholder?: string; value?: string; onChange?: (v: string) => void;
  prefix?: React.ReactNode; suffix?: React.ReactNode; className?: string; type?: string; rows?: number;
}) {
  const sharedStyle: React.CSSProperties = {
    background: C.surfaceHigh,
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    color: C.text,
    width: "100%",
    outline: "none",
    fontSize: 13,
  };
  if (rows) {
    return (
      <textarea
        className={`px-3 py-2 resize-none ${className}`}
        style={sharedStyle}
        placeholder={placeholder}
        value={value}
        rows={rows}
        onChange={(e) => onChange?.(e.target.value)}
      />
    );
  }
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 ${className}`}
      style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12 }}
    >
      {prefix && <span style={{ color: C.textMuted, flexShrink: 0 }}>{prefix}</span>}
      <input
        type={type}
        className="flex-1 bg-transparent outline-none min-w-0"
        style={{ color: C.text, fontSize: 13 }}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
      />
      {suffix && <span style={{ color: C.textMuted, flexShrink: 0 }}>{suffix}</span>}
    </div>
  );
}

// ─── Select ────────────────────────────────────────────────────────────────
export function Select({ value, onChange, options, className = "" }: {
  value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; className?: string;
}) {
  return (
    <select
      className={`px-3 py-2 rounded-xl text-sm outline-none ${className}`}
      style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, color: C.text, width: "100%" }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} style={{ background: C.surfaceHigh }}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── Toggle ────────────────────────────────────────────────────────────────
export function Toggle({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className="w-10 h-5 rounded-full relative transition-all duration-200"
      style={{
        background: value ? C.blue : C.surfaceHigh,
        border: `1px solid ${value ? C.blue : C.border}`,
        boxShadow: value ? `0 0 8px rgba(45,127,249,0.4)` : "none",
      }}
    >
      <span
        className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all duration-200"
        style={{ left: value ? "calc(100% - 18px)" : "2px", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }}
      />
    </button>
  );
}

// ─── Modal wrapper ─────────────────────────────────────────────────────────
export function Modal({ children, onClose, width = "max-w-2xl" }: {
  children: React.ReactNode; onClose: () => void; width?: string;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(7,13,26,0.88)", backdropFilter: "blur(10px)", animation: "slide-up 0.2s ease" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <GlassCard
        className={`w-full ${width} max-h-[90vh] overflow-auto`}
        hover={false}
        style={{ border: `1px solid ${C.borderBright}`, boxShadow: `0 0 60px rgba(45,127,249,0.2)` }}
      >
        {children}
      </GlassCard>
    </div>
  );
}

// ─── ModalHeader ──────────────────────────────────────────────────────────
export function ModalHeader({ title, subtitle, onClose, icon }: {
  title: string; subtitle?: string; onClose: () => void; icon?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: C.border }}>
      <div className="flex items-center gap-3">
        {icon && <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: C.blueDim, border: `1px solid ${C.borderBright}` }}>{icon}</div>}
        <div>
          <h2 className="text-sm font-semibold" style={{ color: C.text }}>{title}</h2>
          {subtitle && <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{subtitle}</p>}
        </div>
      </div>
      <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M1 1l12 12M13 1L1 13" stroke={C.textSub} strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </button>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, color, change, trend }: {
  label: string; value: string; icon: React.ReactNode; color: string; change?: string; trend?: "up" | "down";
}) {
  return (
    <GlassCard className="p-4" hover={false}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs" style={{ color: C.textMuted }}>{label}</span>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}20`, border: `1px solid ${color}30` }}>
          <span style={{ color }}>{icon}</span>
        </div>
      </div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      {change && (
        <div className="flex items-center gap-1 mt-1">
          <span className="text-[10px]" style={{ color: trend === "up" ? C.green : C.red }}>{change}</span>
        </div>
      )}
    </GlassCard>
  );
}

// ─── Brand icons ──────────────────────────────────────────────────────────
export function SlackIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M5.8 15.2a2 2 0 1 1-2-2h2v2z" fill="#E01E5A"/>
      <path d="M6.8 15.2a2 2 0 0 1 4 0v5a2 2 0 0 1-4 0v-5z" fill="#E01E5A"/>
      <path d="M8.8 5.8a2 2 0 1 1 2-2v2h-2z" fill="#36C5F0"/>
      <path d="M8.8 6.8a2 2 0 0 1 0 4H3.8a2 2 0 0 1 0-4h5z" fill="#36C5F0"/>
      <path d="M18.2 8.8a2 2 0 1 1 2 2h-2v-2z" fill="#2EB67D"/>
      <path d="M17.2 8.8a2 2 0 0 1-4 0V3.8a2 2 0 0 1 4 0v5z" fill="#2EB67D"/>
      <path d="M15.2 18.2a2 2 0 1 1-2 2v-2h2z" fill="#ECB22E"/>
      <path d="M15.2 17.2a2 2 0 0 1 0-4h5a2 2 0 0 1 0 4h-5z" fill="#ECB22E"/>
    </svg>
  );
}

export function TeamsIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="2" y="6" width="14" height="14" rx="2" fill="#5059C9"/>
      <circle cx="17" cy="6" r="3" fill="#7B83EB"/>
      <path d="M11 4h6v2a3 3 0 0 1-3 3h0a3 3 0 0 1-3-3V4z" fill="#7B83EB"/>
      <circle cx="8" cy="8" r="3" fill="#fff"/>
    </svg>
  );
}

export function PagerDutyIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="2" y="2" width="20" height="20" rx="4" fill="#06AC38"/>
      <path d="M8 6h5a3 3 0 0 1 0 6H8V6zM8 13h2v5H8z" fill="white"/>
    </svg>
  );
}

export function JiraIcon({ size = 20 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M12 3L3 12l9 9 9-9-9-9z" fill="#2684FF"/>
      <path d="M12 3L6 9l6 6 6-6-6-6z" fill="url(#jira-grad)"/>
      <defs>
        <linearGradient id="jira-grad" x1="6" y1="9" x2="18" y2="9">
          <stop offset="0%" stopColor="#0052CC"/>
          <stop offset="100%" stopColor="#2684FF"/>
        </linearGradient>
      </defs>
    </svg>
  );
}

// ─── SectionHeader ─────────────────────────────────────────────────────────
export function SectionHeader({ title, subtitle, action }: {
  title: string; subtitle?: string; action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between mb-6">
      <div>
        <h2 className="text-lg font-semibold" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="text-xs mt-1" style={{ color: C.textMuted }}>{subtitle}</p>}
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </div>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }: {
  icon: React.ReactNode; title: string; description: string; action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4" style={{ animation: "slide-up 0.3s ease" }}>
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: C.blueDim, border: `1px solid ${C.borderBright}`, animation: "float 3s ease-in-out infinite" }}>
        <span style={{ color: C.blue }}>{icon}</span>
      </div>
      <div className="text-center">
        <div className="text-sm font-medium" style={{ color: C.text }}>{title}</div>
        <div className="text-xs mt-1" style={{ color: C.textMuted }}>{description}</div>
      </div>
      {action}
    </div>
  );
}

// ─── ChipSelect ────────────────────────────────────────────────────────────
export function ChipSelect({
  options, selected, onChange, multi = true, colorFn,
}: {
  options: string[]; selected: string[]; onChange: (v: string[]) => void;
  multi?: boolean; colorFn?: (v: string) => string;
}) {
  const toggle = (v: string) => {
    if (multi) {
      onChange(selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v]);
    } else {
      onChange([v]);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = selected.includes(o);
        const col = colorFn?.(o) ?? C.blue;
        return (
          <button
            key={o}
            onClick={() => toggle(o)}
            className="px-3 py-1 rounded-lg text-xs transition-all duration-200"
            style={{
              background: active ? `${col}20` : C.surfaceHigh,
              border: `1px solid ${active ? col + "50" : C.border}`,
              color: active ? col : C.textMuted,
            }}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
}
